<script setup lang="ts">
import Dialog from 'primevue/dialog';
import welcomeImage from '@/assets/welcome-accounts.png';

// Presentational. The parent opens this once, when the user creates their
// first account.

defineProps<{
  visible: boolean;
  docsUrl: string;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

function close() {
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    :show-header="false"
    :dismissable-mask="true"
    :pt="{
      root: { class: 'welcome-dialog-root' },
      content: { class: 'welcome-dialog-content' },
    }"
    :style="{ width: '440px', maxWidth: 'calc(100vw - 32px)' }"
  >
    <button class="close-x" aria-label="Close" @click="close">
      <i class="pi pi-times" />
    </button>

    <img :src="welcomeImage" alt="" class="hero" />

    <div class="body">
      <h2 class="title">My Accounts</h2>
      <p class="desc">
        You've been invited to help manage an account for Parley API use at MIT. It's a
        responsibility for you to both manage the billing method and the members of this account. An
        account makes sure that people have responsible access to the MIT API for LLM models.
      </p>

      <button class="cta" @click="close">Show me my accounts</button>

      <a class="docs-link" :href="docsUrl" target="_blank" rel="noopener">
        View Parley Documentation
      </a>
    </div>
  </Dialog>
</template>

<style scoped>
:global(.welcome-dialog-root) {
  border-radius: var(--radius-lg);
  overflow: hidden;
}

:global(.welcome-dialog-content) {
  padding: 0 !important;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  /* close-x is positioned against this container. */
  position: relative;
}

.close-x {
  position: absolute;
  top: 21px;
  left: 18px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.close-x:hover {
  background: var(--surface-hover-alt);
}

.close-x .pi {
  font-size: 13px;
}

.hero {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  pointer-events: none;
}

.body {
  padding: 22px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink-2);
}

.cta {
  margin-top: 8px;
  width: 100%;
  padding: 14px 18px;
  background: var(--ink);
  color: var(--surface);
  border: none;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.cta:hover {
  opacity: 0.92;
}

.docs-link {
  align-self: center;
  margin-top: 2px;
  color: var(--ink);
  font-size: 13.5px;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.docs-link:hover {
  opacity: 0.75;
}
</style>
